import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import { filter } from 'rxjs';
import { VxIconComponent } from '../icon/icon.component';
import { VxIconName } from '../icons';
import { sidebarNavItemVariants } from './sidebar-nav-item.variants';

/**
 * A single nav entry:
 *
 *   <vx-sidebar-nav-item link="/incidents" icon="fire" [badge]="4">
 *     Incidents
 *   </vx-sidebar-nav-item>
 *
 * External targets use `href` instead of `link` and open in a new tab:
 *
 *   <vx-sidebar-nav-item href="https://example.com/help" icon="question">
 *     Help
 *   </vx-sidebar-nav-item>
 *
 * Active state comes from the router (`exact` for exact-path matching) or is
 * forced via `[active]` for non-router contexts. Active-state matching is
 * computed from Router directly instead of RouterLinkActive: RLA
 * unconditionally strips `aria-current` when it doesn't own it, clobbering
 * the manual override. In the app sidebar the active item shows the accent
 * indicator flush with the sidebar edge (anchored to vx-sidebar-nav's px-3
 * padding); page-level nav columns turn it off via `[indicator]="false"`.
 */
@Component({
  selector: 'vx-sidebar-nav-item',
  templateUrl: './sidebar-nav-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, VxIconComponent],
  host: { class: 'block' },
})
export class VxSidebarNavItemComponent {
  /** Router target; omit for items handled via (click) at app level. */
  readonly link = input<string | unknown[] | undefined>();

  /**
   * External URL, opened in a new tab. Takes precedence over `link`; such an
   * item never shows the router active state.
   */
  readonly href = input<string>();

  /** Exact-path matching for the router active state. */
  readonly exact = input(false);

  /** Forces the active state regardless of the router. */
  readonly active = input(false);

  /**
   * Accent bar on the active item, flush with the app sidebar's edge. Turn
   * it off for nav columns inside a page (e.g. settings sub-navigation) —
   * the bar is reserved for the app sidebar.
   */
  readonly indicator = input(true);

  readonly icon = input<VxIconName>();

  /**
   * Trailing count pill; hidden when null/undefined. On a compact rail the
   * pill collapses to a dot on the icon's corner, so pass the badge only
   * while it should draw attention (e.g. omit it for a zero count).
   */
  readonly badge = input<string | number | undefined>();

  /**
   * Icon-only form for a collapsed rail: the label stays in the DOM
   * (screen-reader only) so the link keeps its accessible name.
   */
  readonly compact = input(false);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute, { optional: true });

  private readonly navigated = toSignal(
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
    { initialValue: null }
  );

  private readonly routerActive = computed(() => {
    this.navigated();
    const link = this.link();
    if (link == null || this.href()) {
      return false;
    }
    const tree = this.router.createUrlTree(
      Array.isArray(link) ? link : [link],
      {
        relativeTo: this.route,
      }
    );
    const exact = this.exact();
    return this.router.isActive(tree, {
      paths: exact ? 'exact' : 'subset',
      queryParams: exact ? 'exact' : 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  });

  protected readonly isActive = computed(
    () => this.active() || this.routerActive()
  );

  protected readonly hasBadge = computed(() => this.badge() != null);

  protected readonly classes = computed(() => {
    const slots = sidebarNavItemVariants({
      active: this.isActive(),
      compact: this.compact(),
    });
    return {
      link: slots.link(),
      indicator: slots.indicator(),
      icon: slots.icon(),
      label: slots.label(),
      badge: slots.badge(),
      dot: slots.dot(),
    };
  });
}
